import { useEffect, useMemo, useState } from 'react';
import type { ProactiveGreetingPhrase } from '@teo/widget-shared';
import {
  abortProactiveGreetingPrefetch,
  getProactiveGreetingCache,
  isHumanGateEnabled,
  isHumanReady,
  logPopup,
  prefetchProactiveGreeting,
  shouldAllowProactivePopup,
  subscribeProactiveGreeting,
  useWidgetModulesOptional,
  watchPageNavigation,
  whenHumanReady,
  type ProactiveGreetingCacheEntry,
} from '@teo/widget-core';
import { useWidget } from '../context/WidgetContext.js';

export type UseProactiveGreetingOptions = {
  enabled?: boolean;
  apiBase?: string;
  slug: string | null;
  mock?: boolean;
};

export type UseProactiveGreetingResult = {
  pathname: string;
  entry: ProactiveGreetingCacheEntry | null;
  /** Greeting phrases for chat welcome (`POST /greeting`). */
  chatWelcomePhrases: ProactiveGreetingPhrase[];
  /** Joined text for popup bubble. */
  chatWelcomeText: string;
  prefetchStatus: ProactiveGreetingCacheEntry['status'] | 'idle';
};

export function useProactiveGreeting({
  enabled = true,
  apiBase,
  slug,
  mock = false,
}: UseProactiveGreetingOptions): UseProactiveGreetingResult {
  const { locale, settings } = useWidget();
  const modules = useWidgetModulesOptional();
  const greetingEnabled =
    enabled && settings.content.proactiveGreetingEnabled;
  const [pathname, setPathname] = useState(
    () => (typeof window !== 'undefined' ? window.location.pathname : '/'),
  );
  const [entry, setEntry] = useState<ProactiveGreetingCacheEntry | null>(() =>
    slug ? getProactiveGreetingCache(slug, pathname) : null,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    return watchPageNavigation({
      onNavigate: (url) => {
        try {
          setPathname(new URL(url).pathname);
        } catch {
          setPathname(window.location.pathname);
        }
      },
    });
  }, []);

  useEffect(() => {
    if (!greetingEnabled || !apiBase || !slug) {
      setEntry(null);
      return;
    }

    const sync = () => {
      setEntry(getProactiveGreetingCache(slug, pathname));
    };

    sync();
    const unsub = subscribeProactiveGreeting(sync);

    const allowance = shouldAllowProactivePopup({
      mode: settings.content.proactiveGreetingMode,
      reengageSeconds: settings.content.proactiveGreetingReengageSeconds,
      slug,
    });

    if (!allowance.allow) {
      logPopup('greeting-cache', 'skip prefetch — proactive mode gate', {
        pathname,
        reason: allowance.reason,
        mode: settings.content.proactiveGreetingMode,
      });
      abortProactiveGreetingPrefetch(slug, pathname);
      return () => {
        unsub();
      };
    }

    const pageUrl =
      typeof window !== 'undefined' ? window.location.href : undefined;

    let cancelled = false;

    const runPrefetch = () => {
      if (cancelled) return;
      void prefetchProactiveGreeting({
        apiBase,
        slug,
        locale,
        mock,
        url: pageUrl,
        pageContextEnabled: settings.content.contextEnabled,
        flushEventsFirst: true,
        quickReplyCount:
          settings.content.proactiveGreetingQuickReplies &&
          settings.content.proactiveGreetingQuickReplySource !== 'custom'
            ? settings.content.proactiveGreetingQuickReplyCount
            : undefined,
      });
    };

    const humanGateOn =
      (modules?.isEnabled('humanGate') ?? true) && isHumanGateEnabled();
    if (humanGateOn && !isHumanReady()) {
      logPopup('greeting-cache', 'defer prefetch — awaiting human-gate', {
        pathname,
      });
      void whenHumanReady().then(() => {
        if (!cancelled) runPrefetch();
      });
    } else {
      runPrefetch();
    }

    return () => {
      cancelled = true;
      unsub();
      abortProactiveGreetingPrefetch(slug, pathname);
    };
  }, [
    apiBase,
    greetingEnabled,
    locale,
    mock,
    modules,
    pathname,
    settings.content.contextEnabled,
    settings.content.proactiveGreetingMode,
    settings.content.proactiveGreetingQuickReplies,
    settings.content.proactiveGreetingQuickReplyCount,
    settings.content.proactiveGreetingQuickReplySource,
    settings.content.proactiveGreetingReengageSeconds,
    slug,
  ]);

  const chatWelcomePhrases = useMemo(
    () => (entry?.shouldShow ? entry.greetingPhrases : []),
    [entry],
  );

  const chatWelcomeText = entry?.displayText ?? '';

  return {
    pathname,
    entry,
    chatWelcomePhrases,
    chatWelcomeText,
    prefetchStatus: entry?.status ?? 'idle',
  };
}
